import React, { useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { AppDispatch } from '../../definitions/types';
import { withTranslation, WithTranslation } from 'react-i18next';

import DocumentTitle from '../../components/documentTitle/documentTitle';

import { Notification } from '@jimdo/ui';

import DomainPicker from '../../components/domainPicker/domainPicker';
import ViewHeader from '../../components/viewHeader/viewHeader';
import ContentView from '../../components/contentView/contentView';
import Preview from '../../components/preview/preview';
import FreeDomainFirstYearHint from '../../components/hints/freeDomainFirstYearHint';
import {
  validateUsername as validateUsernameAction,
  activeDomainTypeSelected as activeDomainTypeSelectedAction,
  usernameValueChanged as usernameValueChangedAction,
  tldChanged as tldChangedAction,
  domainFieldValueChanged as domainFieldValueChangedAction,
  validateDomain as validateDomainAction,
  continueWithDomain as continueWithDomainAction,
} from '../../redux/actions/domain';
import { setPackage as setPackageAction } from '../../redux/actions/pricing';
import { AppState } from '../../redux/reducers';
import { DomainField } from '../../redux/reducers/domain-field';
import { Prices } from '../../redux/actions/options';
import templateImages, { getTemplateImagesBySubtype } from '../../modules/templateImages';

import './domainView.scss';
import { PACKAGE_TYPE_FREE, PACKAGE_TYPE_PRO } from '../../modules/constants';
import { trackDWH } from '../../modules/tracking';

interface StateProps {
  subtype: string;
  activeType: string;
  error: string;
  free: DomainField;
  prices: Prices;
  product: DomainField;
  transfer: DomainField;
}

interface DispatchProps {
  activeDomainTypeSelected: typeof activeDomainTypeSelectedAction;
  usernameValueChanged: typeof usernameValueChangedAction;
  tldChanged: typeof tldChangedAction;
  setPackage: typeof setPackageAction;
  domainFieldValueChanged: typeof domainFieldValueChangedAction;
  validateDomain: typeof validateDomainAction;
  validateUsername: typeof validateUsernameAction;
  continueWithDomain: typeof continueWithDomainAction;
}

type DomainViewProps = StateProps & DispatchProps & WithTranslation;

export class DomainView extends React.Component<DomainViewProps, {}> {
  mountTime: number = Date.now();

  constructor(props: DomainViewProps) {
    super(props);

    this.propagateUsernameChange = this.propagateUsernameChange.bind(this);
    this.selectDomainType = this.selectDomainType.bind(this);
    this.propagateDomainChange = this.propagateDomainChange.bind(this);
    this.propagateTldChange = this.propagateTldChange.bind(this);
    this.renderFreeDomainPicker = this.renderFreeDomainPicker.bind(this);
    this.renderProductDomainPicker = this.renderProductDomainPicker.bind(this);
    this.renderTransferDomainPicker = this.renderTransferDomainPicker.bind(this);
  }

  componentDidMount(): void {
    trackDWH('domain-view', 'mount', {
      auto: true,
    });
    this.mountTime = Date.now();
  }

  componentWillUnmount(): void {
    trackDWH('domain-view', 'unmount', {
      auto: true,
      mountTimeMs: Date.now() - this.mountTime,
    });
  }

  public propagateDomainChange(event: React.SyntheticEvent<HTMLInputElement>): void {
    const { name, value } = event.currentTarget;
    this.props.domainFieldValueChanged(name, value);
  }

  public propagateTldChange(event: React.FormEvent<HTMLSelectElement>): void {
    const { value } = event.currentTarget;
    this.props.tldChanged(value);
  }

  public propagateUsernameChange(event: React.SyntheticEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;
    this.props.usernameValueChanged(value);
  }

  public selectDomainType(activeType: string): void {
    this.props.activeDomainTypeSelected(activeType);

    if (activeType === PACKAGE_TYPE_FREE) {
      this.props.setPackage(PACKAGE_TYPE_FREE);
      return;
    }

    this.props.setPackage(PACKAGE_TYPE_PRO);
  }

  public render(): JSX.Element {
    const { subtype, error, prices } = this.props;
    const templateImage = getTemplateImagesBySubtype(templateImages, subtype);
    const domainPrice = prices ? prices.domain : '';
    const proPrice = prices ? prices.pro : '';

    return (
      <DocumentTitle title={this.props.t('signup-flow.document-title.domain')}>
        <ContentView className="domain-view-contentwrapper">
          <ViewHeader
            title={this.props.t('signup-flow.domain.view.name')}
            headlineText={this.props.t('signup-flow.domain.headline')}
          />
          <div className="domain-view-contentwrapper__content">
            {error ? (
              <Notification
                type="warning"
                compactLayout
                className="domain-view__notification--error"
                description={this.props.t(error)}
              />
            ) : undefined}

            {this.renderFreeDomainPicker()}
            {this.renderProductDomainPicker()}
            {this.renderTransferDomainPicker()}

            <div className="domain-view-legalnote">
              <FreeDomainFirstYearHint
                className="domain-view-legalnote__domain-first-year"
                domainPrice={domainPrice}
                translationKey={'signup-flow.domainpick.domain-first-year.asterisk-text'}
                proPrice={proPrice}
              />
            </div>
          </div>
          {templateImage ? (
            <Preview desktop={templateImage.desktopImage} mobile={templateImage.mobileImage} />
          ) : null}
        </ContentView>
      </DocumentTitle>
    );
  }

  private renderFreeDomainPicker(): JSX.Element {
    const free = this.props.free;
    return (
      <DomainPicker
        domainType={free.name}
        active={free.name === this.props.activeType}
        neutralState={!this.props.activeType}
        validationPending={free.validationPending}
        showSuccess={free.validated && free.available}
        rawFieldValue={free.rawValue}
        error={free.error}
        hints={free.hints}
        fieldValue={free.value}
        propagateChange={this.propagateUsernameChange}
        validateDomain={this.props.validateUsername}
        canContinue={free.validated && free.available}
        continueWithDomain={this.props.continueWithDomain}
        selectDomainType={this.selectDomainType}
        domainFieldValueChanged={this.props.domainFieldValueChanged}
        placeholder={this.props.t('signup-flow.domain.placeholders')}
      />
    );
  }

  private renderProductDomainPicker(): JSX.Element {
    const product = this.props.product;
    return (
      <DomainPicker
        domainType={product.name}
        active={product.name === this.props.activeType}
        neutralState={!this.props.activeType}
        validationPending={product.validationPending}
        showSuccess={product.validated && product.available}
        error={product.error}
        hints={product.hints}
        fieldValue={product.value}
        prices={this.props.prices}
        tld={product.tld}
        selectDomainType={this.selectDomainType}
        propagateChange={this.propagateDomainChange}
        validateDomain={this.props.validateDomain}
        canContinue={product.validated && product.available}
        continueWithDomain={this.props.continueWithDomain}
        propagateTldChange={this.propagateTldChange}
        placeholder={this.props.t('signup-flow.domain.placeholders')}
        badgeText={this.props.t('signup-flow.pricing.bestseller')}
      />
    );
  }

  private renderTransferDomainPicker(): JSX.Element {
    const transfer = this.props.transfer;

    return (
      <DomainPicker
        domainType={transfer.name}
        validationPending={transfer.validationPending}
        active={transfer.name === this.props.activeType}
        neutralState={!this.props.activeType}
        showSuccess={transfer.validated && transfer.available}
        error={transfer.error}
        fieldValue={transfer.value}
        hints={transfer.hints}
        selectDomainType={this.selectDomainType}
        validateDomain={this.props.validateDomain}
        canContinue={transfer.validated && transfer.available}
        continueWithDomain={this.props.continueWithDomain}
        propagateChange={this.propagateDomainChange}
        placeholder={this.props.t('signup-flow.domain.placeholders')}
      />
    );
  }
}

const mapStateToProps = (state: AppState): StateProps => {
  const {
    options: { prices },
    error,
    domain: {
      activeType,
      fields: { free, product, transfer },
    },
    template: { selectedSubtype },
  } = state;

  return {
    subtype: selectedSubtype,
    activeType,
    error,
    prices,
    free,
    product,
    transfer,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return bindActionCreators(
    {
      usernameValueChanged: usernameValueChangedAction,
      validateUsername: validateUsernameAction,
      activeDomainTypeSelected: activeDomainTypeSelectedAction,
      setPackage: setPackageAction,
      domainFieldValueChanged: domainFieldValueChangedAction,
      tldChanged: tldChangedAction,
      validateDomain: validateDomainAction,
      continueWithDomain: continueWithDomainAction,
    },
    dispatch
  );
};

const TranslatedDomainView = withTranslation()(DomainView);

const ConnectedDomainView = (): JSX.Element => {
  const stateProps = useSelector(mapStateToProps, shallowEqual);
  const dispatch = useDispatch<AppDispatch>();
  const dispatchProps = useMemo(() => mapDispatchToProps(dispatch), [dispatch]);

  return <TranslatedDomainView {...stateProps} {...dispatchProps} />;
};

export default React.memo(ConnectedDomainView);
